import { FunctionComponent, JSX, SyntheticEvent, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Autocomplete from '@mui/material/Autocomplete'
import { countries, CountryType } from './countries'
import styles from './countrySelect.module.scss'
import { useTranslation } from 'react-i18next'
import { AutocompleteRenderInputParams } from '@mui/material'

interface CountrySelectProps {
    country: string
    setCountry?: (country: string) => void
    parent?: boolean
    disabled?: boolean
    readOnly?: boolean
    required?: boolean
}

const CountrySelect: FunctionComponent<CountrySelectProps> = ({
    country,
    setCountry,
    parent,
    disabled,
    readOnly,
    required
}: CountrySelectProps) => {
    const { t } = useTranslation()

    const [value, setValue] = useState<CountryType | null>(
        countries.find((countryType: CountryType) => countryType.code === country) || null
    )

    useEffect(() => {
        setValue(countries.find((countryType: CountryType) => countryType.code === country) || null)
    }, [country])

    const onSetCountry: (newValue: CountryType | null) => void = (newValue: CountryType | null) => {
        const newCountry: string | undefined = countries.find(
            (countryType: CountryType) => countryType.code === newValue?.code
        )?.code
        if (newCountry && newCountry !== country && setCountry) {
            setCountry(newCountry)
        }
    }

    return (
        <Autocomplete
            disabled={disabled}
            readOnly={readOnly}
            options={countries}
            autoHighlight
            getOptionLabel={(option: CountryType): string => option.label}
            renderOption={(props, option: CountryType): JSX.Element => {
                const { key, ...optionProps } = props
                return (
                    <Box key={key} component='li' sx={{ '& > img': { mr: 2, flexShrink: 0 } }} {...optionProps}>
                        <img
                            loading='lazy'
                            width='20'
                            srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                            src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                            alt=''
                        />
                        {option.label} ({option.code})
                    </Box>
                )
            }}
            forcePopupIcon={!readOnly && !disabled}
            renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                <TextField
                    {...params}
                    variant='outlined'
                    rows={1}
                    multiline={true}
                    label={t('country')}
                    required={required}
                    InputProps={{
                        endAdornment: params.InputProps.endAdornment,
                        ref: params.InputProps.ref,
                        className: params.InputProps.className,
                        startAdornment: value && (
                            <img
                                loading='lazy'
                                width='20'
                                srcSet={`https://flagcdn.com/w40/${value.code.toLowerCase()}.png 2x`}
                                src={`https://flagcdn.com/w20/${value.code.toLowerCase()}.png`}
                                alt=''
                                className={styles.flagInput}
                            />
                        )
                    }}
                />
            )}
            className={`${parent ? styles.AutocompleteParent : styles.AutocompleteDaycare} ${styles.Autocomplete}`}
            value={value}
            onChange={(_event: SyntheticEvent, newValue: CountryType | null): void => {
                onSetCountry(newValue)
            }}
        />
    )
}

export default CountrySelect
